// useSSRDataMerge.js
import { useDispatch } from 'react-redux';

import { MERGE_SSR_DATA } from '../ActionTypes.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';

const log = legacySSRDebug.extend('log:useSSRDataMerge');
const errorLog = legacySSRDebug.extend('error:useSSRDataMerge');
let isMerged = false;

// Reset to ensure it only runs once throughout the lifecycle of a component.
const resetSSRDataMerge = () => {
  isMerged = false;
};

/**
 * Hook to merge SSR data into Redux store
 *
 * This hook handles the merging of server-side rendered data into the Redux store.
 * It ensures the merge only happens once per component lifecycle.
 *
 * --------------------------------------------------------------
 * ⚠️ IMPORTANT: Every loader that uses this hook MUST call resetSSRDataMerge()
 * at the top of the loader function to reset state for the new route.
 * Failing to do so will prevent SSR data from being merged on subsequent navigations
 *
 * @example
 * export const loader = async () => {
 *   resetSSRDataMerge(); // ← required
 *   ...
 * };
 *
 * @param {Object} config - Configuration object
 * @param {string} config.componentName - Component name for logging (e.g., 'UserIdLayout', 'StoryCategoriesCategory')
 * @param {Object} config.loaderData - Loader data object containing store data
 * @param {Error} config.loaderError - Loader error object if any
 */
const useSSRDataMerge = ({ componentName, loaderData, loaderError }) => {
  const dispatch = useDispatch();
  if (isMerged) return;

  // Log loader data
  log('loaderData', { loaderData, loaderError, componentName });

  // Merge SSR data
  // We can't use useLayoutEffect here because it will be too late to merge data
  // so that its children will not be able to use the merged data in componentDidMount
  const hasStoreData =
    loaderData?.store && Object.keys(loaderData.store).length > 0;
  if (hasStoreData) {
    isMerged = true;
    if (loaderError) {
      errorLog('error in loader', { error: loaderError, componentName });
    } else {
      try {
        log('merging SSR data', { store: loaderData.store, componentName });
        dispatch({ type: MERGE_SSR_DATA, payload: loaderData.store });
      } catch (restoreReduxError) {
        errorLog('error in restoreRedux', {
          error: restoreReduxError,
          componentName,
        });
      }
    }
  }
};

export { resetSSRDataMerge };
export default useSSRDataMerge;
